import { useEdrsQuery } from "@/lib/queries";
import { useInfringementsQuery } from "@/lib/queries";
import { WidgetShell } from "@/components/dashboard/WidgetShell";
import type { EDRStatus } from "@/types/domain";

function statusColor(status: EDRStatus): string {
  switch (status) {
    case "active":
      return "bg-emerald-500";
    case "degraded":
      return "bg-amber-400";
    case "inactive":
      return "bg-rose-500";
  }
}

function statusLabel(status: EDRStatus): string {
  switch (status) {
    case "active":
      return "Active";
    case "degraded":
      return "Degraded";
    case "inactive":
      return "Inactive";
  }
}

function relativeTime(isoDate: string | null): string {
  if (!isoDate) return "unknown";
  const diffMs = Date.now() - new Date(isoDate).getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return "a few seconds ago";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} h ago`;
  return `${Math.floor(diffH / 24)} d ago`;
}

export function EdrStatusWidget() {
  const { data: edrs, isPending: edrsPending } = useEdrsQuery();
  const { data: infData, isPending: infPending } = useInfringementsQuery();

  const isPending = edrsPending || infPending;

  const countsByEdr = (infData?.infringements ?? []).reduce<
    Record<string, { detections: number; actions: number }>
  >((acc, inf) => {
    const edr = edrs?.find((e) => e.id === inf.edr_id);
    if (!edr) return acc; // No EDR found
    const key = edr.id;
    if (!acc[key]) acc[key] = { detections: 0, actions: 0 };
    acc[key].detections += 1;
    acc[key].actions += inf.action_history?.length ?? 0;
    return acc;
  }, {});

  const activeCount = (edrs ?? []).filter((e) => e.status === "active").length;
  const totalCount = (edrs ?? []).length;

  return (
    <WidgetShell title="EDR Status">
      <div className="flex-1 flex flex-col px-4 py-4 gap-4">
        {/* Summary */}
        <div className="flex items-baseline gap-2">
          <span className="text-5xl font-bold tabular-nums text-primary leading-none">
            {isPending ? "—" : activeCount}
          </span>
          <span className="text-sm text-muted">/ {isPending ? "—" : totalCount} active</span>
        </div>

        {/* Per-EDR rows */}
        <div className="flex flex-col gap-3">
          {isPending
            ? Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="h-14 rounded-lg bg-surface-elevated animate-pulse" />
              ))
            : (edrs ?? []).map((edr) => {
                const counts = countsByEdr[edr.id] ?? { detections: 0, actions: 0 };
                const heartbeatAgo = relativeTime(edr.last_heartbeat);
                return (
                  <div
                    key={edr.id}
                    className="flex items-center gap-3 rounded-lg border border-border px-3 py-2.5 bg-surface-elevated"
                  >
                    {/* Status dot */}
                    <span
                      className={`shrink-0 w-2.5 h-2.5 rounded-full ${statusColor(edr.status)}`}
                      title={statusLabel(edr.status)}
                    />

                    {/* Name + heartbeat */}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary truncate">{edr.label}</p>
                      <p className="text-xs text-muted">{heartbeatAgo}</p>
                    </div>

                    {/* Counts */}
                    <div className="flex gap-4 text-right shrink-0">
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-semibold tabular-nums text-cyan-400">
                          {counts.detections}
                        </span>
                        <span className="text-[10px] text-muted">detections</span>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className="text-sm font-semibold tabular-nums text-violet-400">
                          {counts.actions}
                        </span>
                        <span className="text-[10px] text-muted">actions</span>
                      </div>
                    </div>
                  </div>
                );
              })}
        </div>
      </div>
    </WidgetShell>
  );
}
